import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/config';
import { Table, Button, Modal, Typography, Card, Row, Col, Input, Tag, Space, Avatar, Tooltip } from 'antd';
import { 
  BankOutlined,
  CheckCircleOutlined, 
  ClockCircleOutlined, 
  CloseCircleOutlined, 
  SearchOutlined, 
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MailOutlined,
  PhoneOutlined,
  GlobalOutlined,
  UndoOutlined
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import moment from 'moment';
import IncubatorRegister from '../../components/auth/IncubatorRegister';
import RejectReasonModal from '../../components/admin/RejectReasonModal';

const { Title, Text } = Typography;

const IncubatorManagement = () => {
  const [incubators, setIncubators] = useState([]);
  const [filteredIncubators, setFilteredIncubators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingIncubator, setEditingIncubator] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  // Fetch incubators from API
  const loadIncubators = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/incubators');
      if (response.data && response.data.success) {
        setIncubators(response.data.incubators);
        setFilteredIncubators(response.data.incubators);
      } else {
        throw new Error(response.data?.message || 'Failed to fetch incubators');
      }
    } catch (error) {
      console.error('Error loading incubators:', error);
      toast.error('Error loading incubators. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadIncubators();
  }, []);

  // Search Logic
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);
    const filtered = incubators.filter((incubator) => 
      incubator.incubatorName?.toLowerCase().includes(value) ||
      incubator.contactPerson?.toLowerCase().includes(value) ||
      incubator.email?.toLowerCase().includes(value) ||
      incubator.phone?.includes(value) ||
      incubator.address?.toLowerCase().includes(value) ||
      incubator.focusAreas?.some(area => area.toLowerCase().includes(value))
    );
    setFilteredIncubators(filtered);
  };

  const handleReset = () => {
    setSearchText('');
    setFilteredIncubators(incubators);
  };

  // Toggle status (approved/pending/rejected)
  const handleToggleStatus = async (incubatorId, currentStatus) => {
    const statusOptions = ['approved', 'pending', 'rejected'];
    Modal.confirm({
      title: 'Change Incubator Status',
      content: (
        <div>
          <p>Current status: <Tag color={getStatusColor(currentStatus)}>{currentStatus}</Tag></p>
          <p>Select new status:</p>
        </div>
      ),
      okText: 'Update',
      cancelText: 'Cancel',
      onOk: async () => {
        // For now, toggle between approved and pending
        const newStatus = currentStatus === 'approved' ? 'pending' : 'approved';
        try {
          const response = await api.put(`/admin/incubators/${incubatorId}/status`, {
            status: newStatus
          });
          
          if (response.data && response.data.success) {
            toast.success(`Incubator status updated to ${newStatus}`);
            loadIncubators();
          }
        } catch (error) {
          console.error('Error updating status:', error);
          toast.error('Failed to update status');
        }
      }
    });
  };

  // Approve / Reject registration status
  const handleApprove = (incubatorId) => {
    Modal.confirm({
      title: 'Approve Incubator Registration',
      content: 'Are you sure you want to approve this incubator?',
      okText: 'Approve',
      okType: 'primary',
      onOk: async () => {
        try {
          const response = await api.put(`/admin/incubators/${incubatorId}/approve`);
          if (response.data && response.data.success) {
            toast.success('Incubator approved successfully');
            loadIncubators();
          }
        } catch (error) {
          toast.error('Failed to approve incubator');
        }
      }
    });
  };

  const handleRevert = (incubatorId) => {
    Modal.confirm({
      title: 'Revert to Pending',
      content: 'This will move the record back to Pending review and it will be hidden from the public site if currently approved. Continue?',
      okText: 'Revert',
      onOk: async () => {
        setActionLoading(true);
        try {
          const response = await api.put(`/admin/incubators/${incubatorId}/revert`);
          if (response.data && response.data.success) {
            toast.success('Incubator reverted to pending');
            loadIncubators();
          }
        } catch (error) {
          toast.error('Failed to revert incubator');
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleReject = (incubator) => {
    setRejectTarget(incubator);
    setRejectReason('');
    setRejectModalOpen(true);
  };

  const submitRejection = async () => {
    if (!rejectReason.trim()) {
      toast.warning('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const response = await api.put(`/admin/incubators/${rejectTarget._id}/reject`, {
        reason: rejectReason
      });
      if (response.data && response.data.success) {
        toast.success('Incubator rejected');
        setRejectModalOpen(false);
        loadIncubators();
      }
    } catch (error) {
      toast.error('Failed to reject incubator');
    } finally {
      setActionLoading(false);
    }
  };

  // Modal handlers
  const openAddModal = () => {
    setEditingIncubator(null);
    setIsModalOpen(true);
  };

  const handleEdit = (incubator) => {
    setEditingIncubator(incubator);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setTimeout(() => setEditingIncubator(null), 300);
  };

  const handleEditSuccess = () => {
    handleModalClose();
    loadIncubators();
  };

  // Delete Incubator
  const handleDelete = (incubatorId) => {
    Modal.confirm({
      title: 'Delete Incubator',
      content: 'Are you sure you want to delete this incubator? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const response = await api.delete(`/admin/incubators/${incubatorId}`);
          
          if (response.data && response.data.success) {
            toast.success('Incubator deleted successfully');
            loadIncubators();
          } else {
            throw new Error(response.data?.message || 'Failed to delete incubator');
          }
        } catch (error) {
          toast.error('Failed to delete incubator.');
        }
      }
    });
  };

  // Helper function for status colors
  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  // Statistics Calculation
  const stats = {
    total: incubators.length,
    approved: incubators.filter(i => i.status === 'approved').length,
    active: incubators.filter(i => i.isActive).length,
    inactive: incubators.filter(i => !i.isActive).length
  };

  // Styles based on the screenshot
  const cardStyles = {
    total: { bg: '#e6f7ff', color: '#1890ff', icon: <BankOutlined /> },
    approved: { bg: '#f6ffed', color: '#52c41a', icon: <CheckCircleOutlined /> },
    active: { bg: '#fff7e6', color: '#fa8c16', icon: <CheckCircleOutlined /> },
    inactive: { bg: '#fff1f0', color: '#ff4d4f', icon: <CloseCircleOutlined /> }
  };

  // Table columns
  const columns = [
    {
      title: <span style={{ fontWeight: 500 }}>#</span>,
      key: 'index',
      width: 60,
      render: (text, record, index) => <Text style={{ color: '#666', fontSize:"16px" }}>{index + 1}</Text>,
    },
    {
      title: <span style={{ fontWeight: 500 }}>Incubator Name</span>,
      dataIndex: 'incubatorName',
      key: 'incubatorName',
      render: (text, record) => {
        // Construct proper image URL
        let imageUrl = '';
        if (record.logo) {
          if (record.logo.startsWith('http')) {
            imageUrl = record.logo;
          } else if (record.logo.startsWith('/')) {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}${record.logo}`;
          } else {
            imageUrl = `${process.env.REACT_APP_API_URL || 'http://localhost:3000'}/${record.logo}`;
          }
        }
        
        return (
          <Space>
            {record.logo ? (
              <Avatar src={imageUrl} size={40} />
            ) : (
              <Avatar style={{ backgroundColor: '#3C007A' }} size={40}>
                {text?.charAt(0)?.toUpperCase()}
              </Avatar>
            )}
            <div>
              <Text strong style={{ color: '#333', fontSize:"15px", display: 'block', wordWrap: 'break-word', maxWidth: '200px' }}>{text}</Text>
              <Text type="secondary" style={{ fontSize:"12px" }}>{record.contactPerson || 'N/A'}</Text>
            </div>
          </Space>
        );
      },
    },
    {
      title: <span style={{ fontWeight: 500 }}>Contact Info</span>,
      key: 'contact',
      render: (record) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize:"14px" }}>
            <MailOutlined style={{ marginRight: 5, color: '#1890ff' }} />
            {record.email}
          </Text>
          <Text style={{ fontSize:"14px" }}>
            <PhoneOutlined style={{ marginRight: 5, color: '#52c41a' }} />
            {record.phone}
          </Text>
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Focus Areas</span>,
      dataIndex: 'focusAreas',
      key: 'focusAreas',
      render: (areas) => (
        <Space wrap size={4}>
          {areas && areas.length > 0 ? (
            areas.slice(0, 2).map((area, idx) => (
              <Tag key={idx} color="blue">{area}</Tag>
            ))
          ) : (
            <Text type="secondary">N/A</Text>
          )}
          {areas && areas.length > 2 && (
            <Tag>+{areas.length - 2} more</Tag>
          )}
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Status</span>,
      key: 'status',
      render: (record) => (
        <Space direction="vertical" size={4}>
          <Tag color={getStatusColor(record.status)} icon={record.status === 'approved' ? <CheckCircleOutlined /> : <ClockCircleOutlined />}>
            {record.status}
          </Tag>
          {record.isActive ? (
            <Tag color="green">Active</Tag>
          ) : (
            <Tag color="red">Inactive</Tag>
          )}
        </Space>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Created</span>,
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => (
        <Text style={{ fontSize:"13px", color: '#666' }}>
          {moment(date).format('MMM DD, YYYY')}
        </Text>
      ),
    },
    {
      title: <span style={{ fontWeight: 500 }}>Actions</span>,
      key: 'actions',
      width: 120,
      render: (record) => (
        <Space size="small">
          {record.status === 'pending' && (
            <>
              <Tooltip title="Approve">
                <Button
                  type="link"
                  icon={<CheckCircleOutlined />}
                  onClick={() => handleApprove(record._id)}
                  style={{ color: '#52c41a' }}
                />
              </Tooltip>
              <Tooltip title="Reject">
                <Button
                  type="link"
                  danger
                  icon={<CloseCircleOutlined />}
                  onClick={() => handleReject(record)}
                />
              </Tooltip>
            </>
          )}
          {(record.status === 'approved' || record.status === 'rejected') && (
            <Tooltip title="Revert to Pending">
              <Button
                type="link"
                icon={<UndoOutlined />}
                onClick={() => handleRevert(record._id)}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record._id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px', fontFamily: "'Rubik', sans-serif" }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <Title level={2} style={{ margin: 0, color: '#333', fontFamily: "'Rubik', sans-serif" }}>
          Incubator Management
        </Title>
        <Text type="secondary" style={{ fontFamily: "'Rubik', sans-serif" }}>
          Manage all incubators in the ecosystem
        </Text>
      </div>

      {/* Statistics Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.total.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.total.color }}>{cardStyles.total.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.total.color, display: 'block' }}>
                  {stats.total}
                </Text>
                <Text type="secondary">Total Incubators</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.approved.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.approved.color }}>{cardStyles.approved.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.approved.color, display: 'block' }}>
                  {stats.approved}
                </Text>
                <Text type="secondary">Approved</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.active.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.active.color }}>{cardStyles.active.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.active.color, display: 'block' }}>
                  {stats.active}
                </Text>
                <Text type="secondary">Active</Text>
              </div>
            </Space>
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ background: cardStyles.inactive.bg, border: 'none' }}>
            <Space>
              <div style={{ fontSize: '32px', color: cardStyles.inactive.color }}>{cardStyles.inactive.icon}</div>
              <div>
                <Text style={{ fontSize: '24px', fontWeight: 600, color: cardStyles.inactive.color, display: 'block' }}>
                  {stats.inactive}
                </Text>
                <Text type="secondary">Inactive</Text>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* Search and Filter */}
      <div style={{ 
        background: '#fff', 
        padding: '20px', 
        borderRadius: '8px 8px 0 0', 
        marginBottom: '0px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div style={{ display: 'flex', gap: '8px', flex: 1, minWidth: '200px' }}>
          <Input 
            placeholder="Search incubators by name, contact person, email, phone, or focus area"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={handleSearch}
            style={{ maxWidth: '400px', fontFamily: "'Rubik', sans-serif" }}
            allowClear
          />
          <Button icon={<ReloadOutlined />} onClick={handleReset}>
            Reset
          </Button>
        </div>
        <div className="flex items-center gap-4">
          <Text type="secondary" className="hidden md:block">
            Showing {filteredIncubators.length} of {incubators.length} records
          </Text>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={openAddModal}
            style={{ backgroundColor: '#1890ff', borderRadius: '4px' }}
          >
            Add Incubator
          </Button>
        </div>
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={filteredIncubators}
        loading={loading}
        rowKey={(record) => record._id}
        pagination={{ 
          pageSize: 10, 
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items` 
        }}
        scroll={{ x: 'max-content' }}
        rowClassName="align-middle"
        style={{ background: '#fff', borderRadius: '0 0 8px 8px' }}
      />

      {/* Edit Modal */}
      <Modal
        title={editingIncubator ? 'Edit Incubator' : 'Add New Incubator'}
        open={isModalOpen}
        onCancel={handleModalClose}
        footer={null}
        width={1000}
        centered
        destroyOnClose
      >
        <IncubatorRegister
          editingIncubator={editingIncubator}
          isAdminEdit={true}
          onSuccess={handleEditSuccess}
        />
      </Modal>

      <RejectReasonModal
        open={rejectModalOpen}
        title="Reject Incubator Registration"
        entityLabel={rejectTarget?.incubatorName}
        reason={rejectReason}
        onReasonChange={setRejectReason}
        onOk={submitRejection}
        onCancel={() => setRejectModalOpen(false)}
        confirmLoading={actionLoading}
      />
    </div>
  );
};

export default IncubatorManagement;
